/**
 * Interaction souris/clavier : sélection d'une entité, mode construction
 * avec fantôme coloré (règles de placement partagées, donc même verdict que
 * le serveur), tracé de remontée en deux clics et aperçu de la piste générée.
 * Les commandes partent au serveur ; le fantôme reste affiché « en attente »
 * jusqu'à l'accusé (prédiction optimiste côté client).
 */
import * as THREE from 'three/webgpu';
import {
  BUILDINGS,
  DIFFICULTY_LABEL,
  LIFTS,
  MAX_UPHILL,
  buildingRadius,
  checkBuilding,
  checkLift,
  computeLiftGeometry,
  distPointSegment,
  liftCost,
  planLiftPiste,
  type PlacementContext,
  type Vec2,
} from '@snowchain/shared';
import type { ClientStore } from '../state/store';
import { ui, toast } from '../hud/ui-state';
import type { RtsCamera } from './camera';
import type { AssetLibrary } from './assets';
import type { PisteRaster } from './piste-raster';
import { DIFFICULTY_TINT } from './decor';
import { groomerSpots } from './groomers';

const fmt = (v: number) => Math.round(v).toLocaleString('fr-FR').replace(/\u202f/g, '\u00a0');

export class Interaction {
  readonly group = new THREE.Group();
  private ghost: THREE.Mesh | null = null;
  private ghostKind = '';
  private ghostMat: THREE.MeshBasicNodeMaterial;
  private liftLine: THREE.Line;
  private pisteLine: THREE.Line;
  private hover: THREE.Vector3 | null = null;
  private lastPlanKey = '';
  private planTimer: ReturnType<typeof setTimeout> | null = null;
  private pending = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly cam: RtsCamera,
    private readonly store: ClientStore,
    private readonly lib: AssetLibrary,
    private readonly raster: PisteRaster,
  ) {
    this.group.name = 'interaction';
    this.ghostMat = new THREE.MeshBasicNodeMaterial({ transparent: true, opacity: 0.5, depthWrite: false });
    this.ghostMat.color.setHex(0x7fa98b);
    const lineMat = new THREE.LineBasicNodeMaterial({ color: 0x7fa98b });
    this.liftLine = new THREE.Line(new THREE.BufferGeometry(), lineMat);
    this.liftLine.frustumCulled = false;
    this.liftLine.visible = false;
    this.pisteLine = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicNodeMaterial({ color: 0xffffff }));
    this.pisteLine.frustumCulled = false;
    this.pisteLine.visible = false;
    this.group.add(this.liftLine, this.pisteLine);
    this.bind();
  }

  private context(): PlacementContext | null {
    const r = this.store.replica;
    if (!r) return null;
    return {
      hf: this.store.hf,
      map: this.store.map,
      lifts: r.lifts.values(),
      pistes: r.pistes.values(),
      buildings: r.buildings.values(),
      cash: r.me?.cash,
    };
  }

  private bind(): void {
    this.canvas.addEventListener('pointermove', (e) => {
      if (!ui.build.value || ui.build.value.kind === 'piste') return;
      this.hover = this.cam.pick(e.clientX, e.clientY);
      this.refreshGhost();
    });
    this.canvas.addEventListener('pointerup', (e) => {
      if (e.button !== 0 || this.cam.dragged) return;
      // Tracé ou reprise de piste : l'outil de pistes gère ses propres clics.
      if (ui.build.value?.kind === 'piste' || ui.pisteEdit.value) return;
      const p = this.cam.pick(e.clientX, e.clientY);
      if (!p) return;
      if (ui.build.value) void this.place(p);
      else this.select(p);
    });
    window.addEventListener('keydown', (e) => {
      if ((e.target as HTMLElement | null)?.tagName === 'INPUT') return;
      if (ui.pisteEdit.value) return;
      if (e.code === 'Escape') {
        if (ui.build.value) this.cancelBuild();
        else ui.selection.value = null;
        ui.buildMenu.value = false;
      } else if (e.code === 'KeyR' && ui.build.value?.kind === 'building') {
        const b = ui.build.value;
        ui.build.value = { ...b, rot: b.rot + (e.shiftKey ? -1 : 1) * (Math.PI / 12) };
        this.refreshGhost();
      }
    });
  }

  cancelBuild(): void {
    ui.build.value = null;
    ui.ghost.value = null;
    this.clearGhost();
  }

  private clearGhost(): void {
    if (this.ghost) this.ghost.visible = false;
    this.liftLine.visible = false;
    this.pisteLine.visible = false;
    this.lastPlanKey = '';
  }

  /** Appelé quand le mode construction change (depuis le HUD). */
  onBuildChange(): void {
    // Tracé de piste : l'outil de pistes prend la main, le fantôme de bâtiment disparaît.
    if (!ui.build.value || ui.build.value.kind === 'piste') this.clearGhost();
    this.refreshGhost();
  }

  private ensureGhost(asset: string): THREE.Mesh {
    if (!this.ghost || this.ghostKind !== asset) {
      if (this.ghost) this.group.remove(this.ghost);
      const geo = this.lib.get(asset).geometries[0] as THREE.BufferGeometry;
      this.ghost = new THREE.Mesh(geo, this.ghostMat);
      this.ghost.renderOrder = 5;
      this.ghostKind = asset;
      this.group.add(this.ghost);
    }
    return this.ghost;
  }

  refreshGhost(): void {
    const mode = ui.build.value;
    const ctx = this.context();
    if (!mode || !ctx || mode.kind === 'piste') return;
    const p = this.hover;
    if (mode.kind === 'building') {
      const spec = BUILDINGS[mode.type];
      const g = this.ensureGhost(spec.asset);
      if (!p) {
        g.visible = false;
        return;
      }
      g.visible = true;
      g.position.set(p.x, p.y, p.z);
      g.rotation.y = mode.rot;
      const res = checkBuilding(ctx, mode.type, p.x, p.z, mode.rot);
      this.ghostMat.color.setHex(res.ok ? 0x7fa98b : 0xd0654c);
      ui.ghost.value = res.ok ? { ok: true, cost: res.cost, detail: `Rayon ${Math.round(buildingRadius(mode.type))} m` } : { ok: false, cost: res.cost, reason: res.reason };
      return;
    }
    // Remontée : premier clic = gare aval, second = gare amont.
    if (this.ghost) this.ghost.visible = false;
    if (!p) return;
    if (!mode.bottom) {
      ui.ghost.value = { ok: true, cost: 0, detail: 'Cliquez la gare aval (près du domaine existant)' };
      this.liftLine.visible = false;
      this.pisteLine.visible = false;
      return;
    }
    const top: Vec2 = [p.x, p.z];
    const res = checkLift(ctx, mode.type, mode.bottom, top);
    const geo = computeLiftGeometry(this.store.hf, mode.type, mode.bottom, top);
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i < geo.up.xs.length; i++) pts.push(new THREE.Vector3(geo.up.xs[i], geo.up.ys[i], geo.up.zs[i]));
    this.liftLine.geometry.dispose();
    this.liftLine.geometry = new THREE.BufferGeometry().setFromPoints(pts);
    (this.liftLine.material as THREE.LineBasicNodeMaterial).color.setHex(res.ok ? 0x7fa98b : 0xd0654c);
    this.liftLine.visible = true;
    const len = Math.round(geo.horizontal);
    const capacity = Math.round((3600 / LIFTS[mode.type].interval) * LIFTS[mode.type].seats);
    ui.ghost.value = res.ok
      ? { ok: true, cost: res.cost, detail: `${len} m · dénivelé ${Math.round(geo.rise)} m · ${fmt(capacity)} pers./h` }
      : { ok: false, cost: liftCost(mode.type, len), reason: res.reason };
    // Aperçu de la piste générée, calculé quand le curseur se pose.
    const key = `${mode.type}:${mode.bottom.join(',')}:${Math.round(p.x / 6)}:${Math.round(p.z / 6)}:${mode.difficulty}`;
    if (res.ok && key !== this.lastPlanKey) {
      this.lastPlanKey = key;
      if (this.planTimer) clearTimeout(this.planTimer);
      const bottom = mode.bottom;
      this.planTimer = setTimeout(() => this.previewPiste(mode.type, bottom, top, mode.difficulty), 90);
    } else if (!res.ok) {
      this.pisteLine.visible = false;
    }
  }

  private previewPiste(type: Parameters<typeof planLiftPiste>[2], bottom: Vec2, top: Vec2, difficulty: Parameters<typeof planLiftPiste>[5]): void {
    const r = this.store.replica;
    if (!r) return;
    const plan = planLiftPiste(this.store.hf, r.lifts.values(), type, bottom, top, difficulty);
    const hf = this.store.hf;
    const pts = plan.path.map((q) => new THREE.Vector3(q[0], hf.height(q[0], q[1]) + 1.2, q[1]));
    this.pisteLine.geometry.dispose();
    this.pisteLine.geometry = new THREE.BufferGeometry().setFromPoints(pts);
    const ok = plan.analysis.uphill <= MAX_UPHILL;
    (this.pisteLine.material as THREE.LineBasicNodeMaterial).color.setHex(ok ? (DIFFICULTY_TINT[plan.analysis.difficulty] ?? 0xffffff) : 0xd0654c);
    this.pisteLine.visible = true;
    const g = ui.ghost.value;
    if (g && g.ok) {
      ui.ghost.value = ok
        ? { ...g, detail: `${g.detail ?? ''} · piste ${DIFFICULTY_LABEL[plan.analysis.difficulty].toLowerCase()} de ${Math.round(plan.analysis.length)} m` }
        : { ok: false, cost: g.cost, reason: 'Pas de descente skiable depuis la gare amont' };
    }
  }

  private async place(p: THREE.Vector3): Promise<void> {
    const mode = ui.build.value;
    if (!mode || this.pending || mode.kind === 'piste') return;
    if (!this.store.isOwner) {
      toast('warn', 'Mode observateur', 'Seul le gérant de la station peut construire.');
      return;
    }
    if (mode.kind === 'lift' && !mode.bottom) {
      ui.build.value = { ...mode, bottom: [Math.round(p.x * 10) / 10, Math.round(p.z * 10) / 10] };
      this.refreshGhost();
      return;
    }
    const g = ui.ghost.value;
    if (g && !g.ok) {
      toast('warn', 'Placement impossible', g.reason);
      return;
    }
    this.pending = true;
    const cmd =
      mode.kind === 'building'
        ? ({ type: 'build', kind: mode.type, x: Math.round(p.x * 10) / 10, z: Math.round(p.z * 10) / 10, rot: mode.rot } as const)
        : ({ type: 'buildLift', kind: mode.type, bottom: mode.bottom as Vec2, top: [Math.round(p.x * 10) / 10, Math.round(p.z * 10) / 10] as Vec2, difficulty: mode.difficulty } as const);
    const ack = await this.store.sendCommand(cmd);
    this.pending = false;
    if (ack.ok) {
      const label = mode.kind === 'building' ? BUILDINGS[mode.type].label : LIFTS[mode.type].label;
      toast('ok', 'Chantier lancé', `${label} : ${fmt(g?.cost ?? 0)} €`);
      if (mode.kind === 'lift') {
        ui.build.value = { ...mode, bottom: null };
        this.clearGhost();
      }
    } else {
      toast('danger', 'Construction refusée', ack.error);
    }
  }

  /** Sélection de l'entité la plus proche du point cliqué. */
  private select(p: THREE.Vector3): void {
    const r = this.store.replica;
    if (!r) return;
    let best: { kind: 'lift' | 'piste' | 'building' | 'groomer'; id: number; score: number } | null = null;
    const consider = (kind: 'lift' | 'piste' | 'building' | 'groomer', id: number, score: number) => {
      if (score < 1 && (!best || score < best.score)) best = { kind, id, score };
    };
    // Dameuses : petites et mobiles, elles passent devant ce qu'elles survolent.
    for (const [id, [gx, gz]] of groomerSpots) consider('groomer', id, (Math.hypot(gx - p.x, gz - p.z) / 9) * 0.5);
    for (const b of r.buildings.values()) consider('building', b.id, Math.hypot(b.x - p.x, b.z - p.z) / (buildingRadius(b.kind) + 3));
    for (const l of r.lifts.values()) {
      const st = Math.min(Math.hypot(l.bottom[0] - p.x, l.bottom[1] - p.z), Math.hypot(l.top[0] - p.x, l.top[1] - p.z));
      consider('lift', l.id, Math.min(st / 16, distPointSegment([p.x, p.z], l.bottom, l.top) / 9) * 0.9);
    }
    const s = this.raster.sample(p.x, p.z);
    if (s.piste && s.cov > 0.3) consider('piste', s.piste.id, 0.95);
    ui.selection.value = best ? { kind: (best as { kind: 'lift' | 'piste' | 'building' | 'groomer' }).kind, id: (best as { id: number }).id } : null;
  }
}
